/**
 * ADMIN MODULE — Harbor Ridge demo website (staff only).
 *
 * A stand-in for a CUSTOMER'S OWN website, so the POS ordering embed can be
 * shown working end to end in a sales demo and captured for the marketing site.
 * Everything outside the widget is a prop; the widget is the real production
 * bundle from brandcommand.app talking to the real edge function against the
 * real Harbor Ridge tenant. Orders placed here land in /pos-orders.
 *
 * WHY IT LIVES IN /admin: it is internal staff tooling that customers never see
 * (MODULES.md), and it is registered with the `super_admin` role so it inherits
 * RequireAuth + RequireSuperAdmin. It is reachable only by Brand Command staff
 * who are logged in — which is what keeps a page impersonating a winery off the
 * public internet. It imports nothing from any product module.
 *
 * The Brand Command header is suppressed for this path in App.jsx, or the
 * illusion breaks the moment you screenshot it.
 *
 * ?clean=1 hides the exit pill for a clean capture.
 * ?key=<uuid> overrides the distributor, so this doubles as a way to preview
 * any tenant's order form without leaving the app.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./HarborRidgeDemoSite.css";

// The production embed bundles, same URLs a customer would paste. Deployed by
// `deploy.mjs --embed` alongside the distribution-map embed.
const POS_EMBED_SRC = "https://brandcommand.app/pos-order.js";
const FINDER_EMBED_SRC = "https://brandcommand.app/retail-finder.js";
const SAMPLE_EMBED_SRC = "https://brandcommand.app/sample-request.js";

/**
 * Harbor Ridge's sample-request form. ONE key for the whole tenant, and the
 * contrast with DISTRIBUTORS below is the point rather than an inconsistency.
 *
 * A POS key is per-DISTRIBUTOR because the ship-to is derived from the key, so
 * the key has to say who is ordering. A sample goes somewhere new every time —
 * a prospect, an event, an account that doesn't exist yet — so the requester
 * types the address and there is nothing per-distributor to bind. Migration
 * 0097 makes that structural: a sample key is forbidden from carrying a
 * delivery contact at all.
 *
 * What replaces the derived-address guarantee is the approval gate: a request
 * from this form lands as `approval_status='pending'` and ships to nobody until
 * someone at the brand approves it in /sample-orders.
 */
const SAMPLE_KEY = "70a3dd1d-7367-4f31-abd3-74deea4652b0";

// Harbor Ridge's public "where to buy" widget key (retail_widget_config
// .public_key). Public by construction, demo tenant, origin-locked — same
// reasoning as the order-form keys below.
const FINDER_KEY = "5073cceb-89d2-48e6-a4c2-c11b0aba5386";

/**
 * Harbor Ridge's five demo order forms — one per distributor, exactly as a real
 * customer's would be. These are PUBLIC KEYS by construction (they ship in the
 * page source of whatever site they're installed on) and they belong to the
 * demo tenant, so there is nothing here to protect. Their origin allowlist is
 * limited to this app and localhost.
 */
const DISTRIBUTORS = [
  { key: "6802340e-d377-48b7-9a9a-46ec497a41ff", name: "Pacific Crest Beverage",        state: "California", contact: "Rosa Ibarra" },
  { key: "b82b82fc-da05-4505-adc7-7054b16ed45c", name: "Great Lakes Craft Distributing", state: "Illinois",   contact: "Dev Ramachandran" },
  { key: "d45cad4e-4571-419f-8f41-bde009e64463", name: "Lone Star Fine Wine",            state: "Texas",      contact: "Wade Sanderson" },
  { key: "1e5263c1-039f-450b-89bc-1c4a7f2f8252", name: "Empire Beverage Partners",       state: "New York",   contact: "Colm Fitzgerald" },
  { key: "25cb4e05-011a-43e1-b522-0ecc47c45669", name: "Gulf Coast Selections",          state: "Florida",    contact: "Nadia Okonkwo" },
];

/** Which of the two widgets the page is showing. */
const VIEWS = [
  { id: "order",  label: "Trade Portal — order POS" },
  { id: "sample", label: "Trade Portal — request samples" },
  { id: "find",   label: "Find Us — store locator" },
];

/** The two things a trade visitor can do, shown as tabs inside the portal. */
const TRADE_VIEWS = [
  { id: "order",  label: "Point of Sale" },
  { id: "sample", label: "Sample Request" },
];
const isTrade = (v) => TRADE_VIEWS.some((t) => t.id === v);

/**
 * The handoff specifies Cormorant Garamond + Karla. They are loaded only while
 * this page is mounted rather than added to index.html — a demo prop should not
 * cost every real customer a Google Fonts round trip on every page load. The CSS
 * lists local serif/sans fallbacks, so the page is still readable if the request
 * is blocked or slow.
 */
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Karla:wght@400;500;600&display=swap";

function useHarborRidgeFonts() {
  useEffect(() => {
    if (document.getElementById("hr-fonts")) return undefined;
    const pre = document.createElement("link");
    pre.rel = "preconnect";
    pre.href = "https://fonts.gstatic.com";
    pre.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.id = "hr-fonts";
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(pre);
    document.head.appendChild(link);
    return () => { pre.remove(); link.remove(); };
  }, []);
}

export default function HarborRidgeDemoSite() {
  const [params, setParams] = useSearchParams();
  const clean = params.get("clean") === "1";
  // Validated against VIEWS rather than coerced. This used to read
  // `=== "find" ? "find" : "order"`, which silently mapped every other value —
  // including a third view added later — onto the POS form, so the new tab
  // would have rendered the wrong widget and looked like a routing bug.
  const requested = params.get("view");
  const view = VIEWS.some((v) => v.id === requested) ? requested : "order";
  useHarborRidgeFonts();

  const overrideKey = params.get("key");
  const selected = useMemo(
    () => DISTRIBUTORS.find((d) => d.key === overrideKey) ?? DISTRIBUTORS[0],
    [overrideKey]
  );
  const activeKey = overrideKey || selected.key;

  return (
    <div className="hr-site">
      {!clean && (
        <div className="hr-exit">
          <Link to="/">← Brand Command</Link>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("clean", "1");
              setParams(next, { replace: true });
            }}
          >
            Hide for screenshot
          </button>
        </div>
      )}

      <header className="hr-nav">
        <div className="hr-nav-inner">
          <div className="hr-brand">
            {/* Logomark from the design handoff: a wine-outlined circle holding
                a gold square rotated 45°. */}
            <span className="hr-mark" aria-hidden="true"><span /></span>
            <span className="hr-wordmark">
              <b>Harbor Ridge</b>
              <small>Est. 1994 · Sonoma Coast</small>
            </span>
          </div>
          <nav className="hr-links" aria-label="Harbor Ridge">
            <span>Our Wines</span>
            <span>Vineyard</span>
            <button
              type="button"
              className={view === "find" ? "is-current" : ""}
              onClick={() => setView(setParams, params, "find")}
            >
              Find Us
            </button>
            <button
              type="button"
              className={isTrade(view) ? "is-current" : ""}
              onClick={() => setView(setParams, params, "order")}
            >
              Trade Portal
            </button>
          </nav>
        </div>
      </header>

      {/* The two trade forms live as tabs inside the portal bar rather than as a
          hover menu on "Trade Portal". A real trade portal is a place you land
          and then choose within, and a hover dropdown is unusable on the phone
          a rep is actually holding in a back office. */}
      {isTrade(view) && (
        <div className="hr-portalbar">
          <div className="hr-portalbar-inner">
            <span className="hr-portalbar-label">Trade Portal</span>
            <span className="hr-portaltabs" role="tablist" aria-label="Trade Portal">
              {TRADE_VIEWS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={view === t.id}
                  className={`hr-portaltab${view === t.id ? " is-current" : ""}`}
                  onClick={() => setView(setParams, params, t.id)}
                >
                  {t.label}
                </button>
              ))}
            </span>
            {/* Only the POS form knows who you are — that is the whole
                difference between the two keys, so the demo shows it rather
                than papering over it with a generic greeting. */}
            {view === "order" && (
              <span className="hr-portalbar-who">
                Signed in as <strong>{selected.contact}</strong> · {selected.name} ({selected.state})
              </span>
            )}
          </div>
        </div>
      )}

      <main className="hr-main">
        {view === "order" ? (
          <>
            <div className="hr-intro">
              <p className="hr-eyebrow">Trade Program · Autumn 2026</p>
              <h1>Point-of-sale materials</h1>
              <p>
                Everything your floor team needs to tell the Harbor Ridge story.
                Order below — kits ship from the Santa Rosa warehouse, freight
                prepaid on orders over twelve units.
              </p>
            </div>

            <EmbedWidget src={POS_EMBED_SRC} embedKey={activeKey} />

            {!clean && (
              <div className="hr-switcher">
                <label>
                  <span>Demo: view as</span>
                  <select
                    value={activeKey}
                    onChange={(e) => {
                      const next = new URLSearchParams(params);
                      next.set("key", e.target.value);
                      setParams(next, { replace: true });
                    }}
                  >
                    {DISTRIBUTORS.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.name} — {d.state}
                      </option>
                    ))}
                  </select>
                </label>
                <p>
                  Each distributor sees their own form, from their own key. The
                  ship-to address comes from the key — nothing on this page can
                  change it, which is what stops one distributor ordering as
                  another.
                </p>
              </div>
            )}
          </>
        ) : view === "sample" ? (
          <>
            <div className="hr-intro">
              <p className="hr-eyebrow">Trade Program · Autumn 2026</p>
              <h1>Request samples</h1>
              <p>
                Tasting a wine sells it. Tell us what you'd like and where to
                send it — a buyer meeting, a staff training, an account we
                haven't opened yet. Requests are reviewed before anything ships.
              </p>
            </div>

            <EmbedWidget src={SAMPLE_EMBED_SRC} embedKey={SAMPLE_KEY} />

            {!clean && (
              <div className="hr-switcher">
                <p style={{ margin: 0 }}>
                  No distributor picker here, and that is the design rather than
                  an omission. The POS form derives its ship-to from a
                  per-distributor key, so nothing on the page can redirect an
                  order. A sample goes somewhere new every time, so the requester
                  types the address — and the control that replaces the derived
                  one is approval: this lands in Harbor Ridge's queue as
                  <strong> pending</strong> and ships to nobody until a human
                  says yes. One key serves the whole brand.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="hr-intro">
              <p className="hr-eyebrow">Stockists · Sonoma Coast AVA</p>
              <h1>Find Harbor Ridge near you</h1>
              <p>
                Search by ZIP or city to find your closest stockist. Our wines
                are poured and sold at independent shops and restaurants across
                California, Illinois, Texas, New York and Florida.
              </p>
            </div>

            {/* A default area so the demo opens with pins on the map instead of
                an empty United States. A real customer would usually leave this
                off and let the visitor search — but "here is our store finder,
                now watch me type something into it" is a poor opening line.
                50 mi from San Francisco reaches Berkeley and Napa, so it lands
                on four stockists rather than one. */}
            <EmbedWidget
              src={FINDER_EMBED_SRC}
              embedKey={FINDER_KEY}
              plain
              attrs={{ "data-default": "San Francisco, CA", "data-radius": "50" }}
            />

            {!clean && (
              <div className="hr-switcher">
                <p style={{ margin: 0 }}>
                  This one is public — no login. It reads the same store list
                  staff maintain in Brand Command, returns only the nearest
                  matches inside the radius, and is locked to the websites the
                  brand has named. 23 demo stockists are loaded: try{" "}
                  <strong>94111</strong>, <strong>Chicago, IL</strong>,{" "}
                  <strong>Austin, TX</strong>, <strong>10014</strong> or{" "}
                  <strong>Miami, FL</strong> — or type <strong>Springfield</strong>{" "}
                  to see how it handles an ambiguous city.
                </p>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="hr-foot">
        <div className="hr-foot-inner">
          <div>
            <p className="hr-foot-brand">Harbor Ridge</p>
            <p>
              1420 Ridgeway Road, Occidental, California — Sonoma Coast AVA.
              Tasting room open Thursday through Sunday.
            </p>
          </div>
          <div>
            <h4>Artwork</h4>
            <ul>
              <li>Download spec sheets</li>
              <li>Logo &amp; label files</li>
              <li>Print guidelines</li>
            </ul>
          </div>
          <div>
            <h4>Trade support</h4>
            <ul>
              <li>trade@harborridge.example</li>
              <li>(707) 555-0148</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

function setView(setParams, params, id) {
  const next = new URLSearchParams(params);
  next.set("view", id);
  setParams(next, { replace: true });
}

/**
 * Mounts a real embed exactly as a customer's page would: a <script> tag with a
 * data-key. Both bundles read `document.currentScript` at execution time, so it
 * has to be a genuine injected script element rather than a fetch-and-eval —
 * which also means this is a true test of the shipped artifact, not a re-render
 * of it.
 *
 * Re-mounts cleanly when the key or bundle changes (and under StrictMode's
 * double-invoke): the widgets append their own DOM as a sibling of the script
 * and have no teardown of their own, so the container is emptied first.
 *
 * `plain` drops the card chrome — the store finder draws its own bordered
 * container inside a shadow root, so wrapping it in a second one looks doubled.
 */
function EmbedWidget({ src, embedKey, plain, attrs }) {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);
  // Serialised so the effect re-runs on a real change rather than on every
  // render (a fresh object literal is a new identity each time).
  const attrKey = JSON.stringify(attrs ?? {});

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    setFailed(false);
    host.textContent = "";

    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    script.setAttribute("data-key", embedKey);
    for (const [k, v] of Object.entries(JSON.parse(attrKey))) script.setAttribute(k, v);
    script.onerror = () => setFailed(true);
    host.appendChild(script);

    return () => { host.textContent = ""; };
  }, [src, embedKey, attrKey]);

  return (
    <section className={plain ? "hr-widget-plain" : "hr-widget"}>
      <div ref={hostRef} />
      {failed && (
        <p className="hr-widget-err">
          Couldn’t load the embed bundle from {src}. Run
          <code> node deploy.mjs --embed</code> if it hasn’t been deployed yet.
        </p>
      )}
    </section>
  );
}
